import { Typography } from "@kelp-dao/kernel-design-system";
import UTMLink from "@components/UTMLink";
import { DeFiRow } from "@container/pages/defi/type";
import useDefiData, { DEFI_FILTER_VARIANT } from "@hooks/useDefiData";
import { DefiItemCard } from "./DefiItemCard";

interface DefiPartnershipsProps {
  asset: string;
}

const DefiPartnerships = ({ asset }: DefiPartnershipsProps) => {
  // Asset to variant mapping for better maintainability
  const assetVariantMap: Record<string, DEFI_FILTER_VARIANT> = {
    ageth: DEFI_FILTER_VARIANT.AGETH_VAULT_PARTNERSHIPS,
    rseth: DEFI_FILTER_VARIANT.SDUSD_VAULT_PARTNERSHIPS,
    hgeth: DEFI_FILTER_VARIANT.HGETH_VAULT_PARTNERSHIPS,
    // Add more assets here as needed
  };

  // Asset to display name mapping for UTM links
  const assetDisplayMap: Record<string, string> = {
    ageth: "agETH",
    rseth: "rseth",
    hgeth: "hgETH",
    // Add more assets here as needed
  };

  const defiData = useDefiData({
    type: "vault",
    variant: assetVariantMap[asset.toLowerCase()],
  }) as DeFiRow[];

  if (defiData?.length > 0) {
    return (
      <div className="bg-bg-card rounded-xl mb-5 w-full border border-border-default">
        {/* Header Section */}
        <div className="flex justify-between items-center p-4 tablet:p-6 ">
          <Typography
            type="heading"
            variant="small"
            className="text-lg tablet:text-xl font-bold text-text-primary tracking-tight"
          >
            DeFi
          </Typography>
          {defiData.length > 3 && (
            <UTMLink
              href={`/defi?asset=${assetDisplayMap[asset.toLowerCase()]}`}
              isExternal={false}
            >
              <button className="px-3 py-1.5 text-xs font-medium text-button-primary-bg bg-bg-card border border-border-default rounded-lg hover:bg-button-secondary-hover  transition-colors">
                View all
              </button>
            </UTMLink>
          )}
        </div>

        {/* Content Section */}
        <div className="p-4 tablet:p-6">
          {/* Mobile and Small screens: Single card with all items and dividers */}
          <div className="block tablet:hidden">
            <div className="bg-bg-card">
              {defiData.slice(0, 4).map((item: DeFiRow, idx: number) => (
                <div key={`partn_${idx}`}>
                  <DefiItemCard
                    row={item}
                    cardCount={defiData.length}
                    isMobile={true}
                    showBorder={false}
                  />
                  {idx < 1 && (
                    <div className="border-t border-border-default my-4"></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Medium screens and up: Horizontal layout */}
          <div
            className={`hidden tablet:flex gap-4 ${
              defiData.length <= 3
                ? "justify-start"
                : "overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
            }`}
          >
            {defiData.slice(0, 4).map((item: DeFiRow, idx: number) => (
              <DefiItemCard
                key={`partn_${idx}`}
                row={item}
                cardCount={defiData.length}
                isMobile={false}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default DefiPartnerships;
